import { AxiosInstance, AxiosResponse, AxiosError } from "axios";

export interface ApiResponse<T = unknown> {
  type: number;
  message: string;
  data: T;
}

export class ApiInterceptors {
  private token: string | null = null;

  constructor(private readonly api: AxiosInstance) {
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      config => {
        // Add auth token if available
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // Log request in development mode
        if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOGGING === "true") {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error: AxiosError) => {
        if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOGGING === "true") {
          console.error("[API Request Error]", error);
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response in development mode
        if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOGGING === "true") {
          console.log(`[API Response] ${response.status} ${response.config.url}`, {
            data: response.data,
          });
        }

        return response;
      },
      (error: AxiosError) => {
        // Handle authentication errors
        if (error.response?.status === 401) {
          this.handleAuthError();
        }

        // Handle other errors
        if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOGGING === "true") {
          console.error("[API Response Error]", {
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            data: error.response?.data,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private handleAuthError() {
    // Clear token and redirect to login
    this.token = null;
    localStorage.removeItem("admin-token");

    // Only redirect if not already on login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  setAuthToken(token: string) {
    this.token = token;
    localStorage.setItem("admin-token", token);
    this.api.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  clearAuthToken() {
    this.token = null;
    localStorage.removeItem("admin-token");
    delete this.api.defaults.headers.common.Authorization;
  }

  getAuthToken(): string | null {
    return this.token;
  }

  // Load token from localStorage on initialization
  loadStoredToken() {
    const storedToken = localStorage.getItem("admin-token");
    if (storedToken) {
      this.setAuthToken(storedToken);
    }
  }
}
